// Modules
import React, { useMemo } from 'react';

// Table Columns
import { AoyColumns } from './AoyColumns';

// Components
import AnglerExpanded from '../../components/AnglerExpanded/AnglerExpanded';
import Message from '../../components/Message/Message';
import ReactTable from '../../components/ReactTable/ReactTable';

// Config
import { isEmpty } from 'lodash';
import { CellProps } from 'react-table';
import AdWrapper from '../../components/Advertisement/AdWrapper';
import { CLASS_PREFIX, DEFAULT_STRING_NO_TABLE_RESULTS } from '../../config';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import {
  AdvertisementVariant,
  IAoyTableData,
  ICatchesTableData,
} from '../../types';
import { basstrakkCatchesTop5 } from '../../util/processing';
import { classList } from '../../util/util';

interface IAoyTable {
  data: any[];
  tabLabel: string;
  tabClass: string;
}

const AoyTable = (props: IAoyTable) => {
  const { data } = props;
  const headerColumns = useMemo(() => AoyColumns, []);

  const { width } = useWindowDimensions();

  // We have a default message when there is no data
  const shouldShow = isEmpty(data);

  const renderRow = ({ row, index }: CellProps<IAoyTableData>) => {
    // When a row is expanded to show the Angler's information we want to
    // change the color of the row.
    const expandedRowColor = row.isExpanded
      ? {
          style: {
            background: '#f9ba5e',
          },
        }
      : {};

    const stripingClass = index % 2 ? 'even' : 'odd';

    return (
      <div
        // We can use the getToggleRowExpandedProps prop-getter
        // to build the toggle for the expanded row
        {...row.getToggleRowExpandedProps(expandedRowColor)}
        key={`aoy-${row.id}`}
        className={classList(
          `${CLASS_PREFIX}row row-index-${row.index} index-${index}`
        )}
      >
        {row.cells.map(cell => {
          return (
            <div
              {...cell.getCellProps()}
              className={classList(
                (cell.column as any).className,
                stripingClass
              )}
            >
              {cell.render('Cell', { index, width })}
            </div>
          );
        })}
      </div>
    );
  };

  // Allows rendering an "expanded" row below a default table row.
  const renderExpanded = ({ row, index }: CellProps<IAoyTableData>) => {
    const { original: angler } = row;
    return (
      <AnglerExpanded
        anglerId={angler.FISHERMENID}
        anglerName={angler.FISHERMAN_PHONENAME} // Must have anglerName now to retrieve videos from BrightCove.
        catchData={basstrakkCatchesTop5(angler.FISHERMENID, angler.Catches)}
        count={index}
      />
    );
  };

  const renderRowSeparator = ({
    numRows,
    index,
  }: CellProps<ICatchesTableData>) => {
    const modulo = 15;
    const foo = index % modulo === 0;

    if (numRows < modulo) {
      return (
        <AdWrapper variant={AdvertisementVariant.AOY} count={index / modulo} />
      );
    }

    return (
      <>
        {/* Display an ad every 15 rows */}
        {index !== 0 && foo ? (
          <AdWrapper
            variant={AdvertisementVariant.AOY}
            count={index / modulo}
          />
        ) : null}
      </>
    );
  };
  return (
    <>
      {shouldShow ? (
        <Message
          message={DEFAULT_STRING_NO_TABLE_RESULTS}
          customClass="no-results-message"
          shouldShow={shouldShow}
        />
      ) : (
        <ReactTable
          tableName="aoy"
          columns={headerColumns}
          data={data}
          initialState={{
            sortBy: [
              {
                id: 'aoyRank',
                desc: false,
              },
              {
                id: 'aoyPts',
                desc: true,
              },
            ],
          }}
          renderExpanded={renderExpanded}
          renderRow={renderRow}
          renderRowSeparator={renderRowSeparator}
          classPrefix={CLASS_PREFIX}
        />
      )}
    </>
  );
};

export default AoyTable;
